import { Link, Outlet, useParams } from "react-router-dom";
import InstagramIcon from "../../assets/svgs/instagram.svg";
import { Badge } from "../../components/core/badge.core";
import MetricCard from "../../components/ui/metric-card.ui";
import { useGetProposalByIdHttp } from "../../hooks/http/proposals/get-proposal-by-id.hook";
import type { IProposal } from "../../types/models/proposal.modals";
import { useEffect, useState } from "react";
import { handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import Spinner from "../../../components/spinner.components";
import { useUserStore } from "../../../store/user.store";
import { useGetProposalByIdUnauthenticatedHttp } from "../../hooks/http/proposals/get-proposal-by-id-unauthenticated.hook";
import ProposalSocialsTable from "./proposal-socials.table";
import { useGetProposalSocialsHttp } from "../../hooks/http/proposal-socials/get-proposal-socials.hook";
import { useGetProposalSocialsUnauthenticatedHttp } from "../../hooks/http/proposal-socials/get-proposal-socials-unauthenticated.hook";
import useProposalSocialStore from "../../store/proposal-social.store";
import Button from "../../components/core/button.core";
import FilterIcon from "../../../assets/svgs/common/filter.svg";
import BoostIcon from "../../../assets/svgs/common/boost.svg";

export default function ProposalPartial() {
	const { execute: getProposalByIdHttp, fetching: fetchingProposal } =
		useGetProposalByIdHttp();
	const {
		execute: getProposalByIdUnauthenticatedHttp,
		fetching: fetchingProposalUnauthenticated,
	} = useGetProposalByIdUnauthenticatedHttp();
	const { setData: setProposalSocials, data: proposalSocials } =
		useProposalSocialStore();

	const { execute: getProposalSocialsHttp, fetching: fetchingProposalSocials } =
		useGetProposalSocialsHttp();
	const {
		execute: getProposalSocialsUnauthenticatedHttp,
		fetching: fetchingProposalSocialsUnauthenticated,
	} = useGetProposalSocialsUnauthenticatedHttp();
	const { token } = useUserStore();
	const { proposalId } = useParams();
	const [proposal, setProposal] = useState<IProposal>();
	const { toast } = useToast();

	async function getProposalSocials() {
		if (!proposalId) {
			return;
		}
		const response = await getProposalSocialsHttp({
			body: null,
			pathParams: { proposalId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setProposalSocials(response.data.campaignProposalSocials);
	}

	async function getProposalSocialsUnauthenticated() {
		if (!proposalId) {
			return;
		}
		const response = await getProposalSocialsUnauthenticatedHttp({
			body: null,
			queryParams: { proposal: proposalId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setProposalSocials(response.data.campaignProposalSocials);
	}

	async function getProposalUnauthenticated() {
		if (!proposalId) {
			return;
		}
		const response = await getProposalByIdUnauthenticatedHttp({
			body: null,
			queryParams: { id: proposalId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setProposal(response.data.proposal);
	}
	async function getProposal() {
		if (!proposalId) {
			return;
		}
		const response = await getProposalByIdHttp({
			body: null,
			pathParams: { proposalId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setProposal(response.data.proposal);
	}
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (token) {
			getProposal();
			getProposalSocials();
		} else {
			getProposalUnauthenticated();
			getProposalSocialsUnauthenticated();
		}
	}, [proposalId]);

	function costPerView() {
		if (
			proposal?.expectedMetrics.cost !== undefined &&
			proposal?.expectedMetrics.views !== undefined &&
			proposal?.expectedMetrics.views !== 0
		) {
			return proposal.expectedMetrics.cost / proposal.expectedMetrics.views;
		}
		return undefined;
	}

	if (fetchingProposal || fetchingProposalUnauthenticated) {
		return (
			<div className="flex justify-center items-center w-full h-screen">
				<Spinner className="text-black" width={20} />
			</div>
		);
	}
	return (
		<div className="relative flex flex-col justify-between bg-white w-full h-svh font-cabinetgrotesk text-soloprimary">
			<div className="flex flex-col px-6 pt-12 w-full">
				<div className="flex items-center gap-2">
					<Badge
						className="flex items-center gap-1 w-fit capitalize"
						variant="outline"
					>
						<InstagramIcon />
						{proposal?.socialPlatform}
					</Badge>
					<Badge className="w-fit capitalize" variant="outline">
						{proposal?.socialCreatorType}
					</Badge>
					<Badge className="w-fit capitalize" variant="outline">
						{proposal?.socialPostType}
					</Badge>
				</div>
				<h1 className="pt-3 font-semibold text-3xl">{proposal?.name}</h1>
				<p className="py-5 font-figtree text-base text-left">
					{proposal?.brief}
				</p>
			</div>
			<div className="w-full">
				<div className="items-center gap-3 md:gap-5 grid grid-cols-2 md:grid-cols-4 px-6 py-8 border-t border-b border-black w-full">
					{[
						{
							title: "Cost",
							value: proposal?.expectedMetrics.cost,
							prefix: "₹",
						},
						{
							title: "Expected views",
							value: proposal?.expectedMetrics.views,
						},
						{
							title: "Expected posts",
							value: proposal?.expectedMetrics.posts,
						},
						{
							title: "Cost per view",
							value: costPerView(),
							prefix: "₹",
							precision: true,
						},
					].map((item) => (
						<MetricCard key={item.title} {...item} />
					))}
				</div>
			</div>
			<ProposalSocialsTable
				data={proposalSocials || []}
				fetchingProposalSocials={
					fetchingProposalSocials || fetchingProposalSocialsUnauthenticated
				}
			/>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				id="getProposalSocials"
				onClick={() => {
					getProposalSocials();
				}}
				className="hidden"
			/>
			<Outlet />
			<div className="right-0 bottom-0 z-10 sticky bg-white border-t border-black w-full">
				<div className="flex justify-end items-center gap-5 px-5 py-4 w-full">
					{proposalId ? (
						<Button
							onClick={() => {
								if (!token) {
									return toast({
										content: "Please login to share",
										toastType: "error",
									});
								}
								navigator.clipboard.writeText(
									`${window.location.origin}/brands/proposal/${proposalId}`,
								);
								toast({
									content: "Proposal Link Copied",
									toastType: "success",
								});
							}}
							variant="primary"
							className="px-6 py-4 font-semibold text-sm"
						>
							<BoostIcon className="pr-2 w-6 h-6" />
							Share Proposal
						</Button>
					) : null}
					{proposalId ? (
						<Link
							onClick={() => {
								if (!token) {
									toast({
										content: "Please login to manage filters",
										toastType: "error",
									});
								}
							}}
							to={
								token
									? "manage-filters"
									: `/brands/auth/login?proposalId=${proposalId}`
							}
						>
							<Button
								variant="secondary"
								className="flex items-center gap-1 px-6 py-4 font-semibold text-sm"
							>
								<FilterIcon />
								Manage filters
							</Button>
						</Link>
					) : null}
				</div>
			</div>
		</div>
	);
}
